import { PT_CITIES } from '@powerful/config/geo';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LocationState } from '../../lib/geo/use-consumer-location';
import { Icon } from '../icons/icon';
import { OverlayDialog } from './overlay-dialog';

/**
 * Seletor de localização (canvas `Location` / `LocationDenied`, C4).
 *
 * A lista de cidades está visível DESDE o início — não escondida atrás de uma recusa do GPS
 * (mesma decisão do onboarding, RN-3/RN-4). Quem já sabe que vai recusar não é obrigado a pedir
 * primeiro. Rádios nativos: as setas mudam a seleção, logo aplicar é um passo explícito.
 */
export interface CityPickerProps {
  open: boolean;
  onClose: () => void;
  selectedCityId: string | null;
  status: LocationState;
  onChooseCity: (cityId: string) => void;
  onUseDevice: () => Promise<boolean>;
}

const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function CityPicker({ open, onClose, selectedCityId, status, onChooseCity, onUseDevice }: CityPickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string | null>(selectedCityId);
  const queryId = useId();
  const name = useId();

  // Reabrir parte da escolha em vigor, não do rascunho abandonado da última vez.
  useEffect(() => {
    if (open) {
      setDraft(selectedCityId);
      setQuery('');
    }
  }, [open, selectedCityId]);

  const cities = useMemo(() => {
    const q = fold(query);
    return q ? PT_CITIES.filter((c) => fold(c.name).includes(q) || fold(c.district).includes(q)) : PT_CITIES;
  }, [query]);

  const requesting = status === 'requesting';
  const blocked = status === 'denied' || status === 'unavailable';

  const askDevice = async () => {
    if (requesting) return;
    if (await onUseDevice()) onClose();
  };

  const apply = () => {
    if (draft) onChooseCity(draft);
    onClose();
  };

  return (
    <OverlayDialog
      open={open}
      onClose={onClose}
      title={t('discovery.location.title')}
      testId="city-picker"
      footer={
        <button className="btn btn-secondary" type="button" onClick={apply}>
          {t('discovery.location.apply')}
        </button>
      }
    >
      {blocked ? (
        <div className="alert alert-warn min-w-0" role="alert">
          <span className="text-warn"><Icon name="pin" size={18} /></span>
          <span className="min-w-0 text-[length:var(--t-sm)]">{t(`discovery.location.${status}`)}</span>
        </div>
      ) : (
        <div className="pc-sec gap-2">
          <button className="btn btn-secondary btn-full" type="button" onClick={askDevice} aria-disabled={requesting || undefined}>
            {requesting ? <span className="pc-spin" aria-hidden="true" /> : <Icon name="locate" size={18} />}
            <span>{requesting ? t('discovery.location.requesting') : t('discovery.location.useMine')}</span>
          </button>
          <p className="pc-muted">{t('discovery.location.coarse')}</p>
        </div>
      )}

      <div className="field">
        <label htmlFor={queryId}>{t('discovery.location.searchCity')}</label>
        <div className="input">
          <Icon name="search" size={18} />
          <input
            id={queryId}
            type="search"
            value={query}
            placeholder={t('discovery.location.searchPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      <fieldset className="pc-fs">
        <legend className="pc-vh">{t('discovery.location.cities')}</legend>
        {cities.length === 0 ? (
          <p className="pc-muted" role="status">{t('discovery.location.noCity', { query: query.trim() })}</p>
        ) : (
          <ul className="pc-cities">
            {cities.map((city) => {
              const checked = draft === city.id;
              return (
                <li key={city.id}>
                  <label className="pc-city">
                    <span className="flex min-w-0 flex-col">
                      <span>{city.name}</span>
                      {city.district !== city.name ? <span className="pc-city-sub">{city.district}</span> : null}
                    </span>
                    <span className="hit -mr-[11px]">
                      <input className="pc-vh" type="radio" name={name} value={city.id} checked={checked} onChange={() => setDraft(city.id)} />
                      <span className={checked ? 'radio on' : 'radio'} aria-hidden="true" />
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </fieldset>
    </OverlayDialog>
  );
}
